import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Switch,
  Typography,
  TextField,
} from "@material-ui/core";
import React from "react";
import { makeStyles } from "@material-ui/styles";
import { Formik } from "formik";
import CloseIcon from "@material-ui/icons/CloseRounded";
import { groupByCategory } from "../../utils/bundle-modules";

const useStyle = makeStyles((theme) => ({
  scrollContainer: {
    maxHeight: "70vh",
    overflowY: "auto",
    backgroundColor: "#f3f3f3",
  },
  moduleCard: {
    padding: theme.spacing(1.5),
    height: "100%",
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,.12)",
  },
  moduleCardOn: {
    padding: theme.spacing(1.5),
    height: "100%",
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,.12)",
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(25,118,210,.08)",
  },
  badge: {
    marginLeft: theme.spacing(0.5),
  },
}));

const validate = (values) => {
  const errors = {};
  if (!values.name || !values.name.trim()) {
    errors.name = "Name is required";
  }
  return errors;
};

export default function BundleAddDialogComponent({
  moduleListing,
  open,
  onClose,
  onSubmit,
  isLoading,
  prefill,
}) {
  const classes = useStyle();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      fullWidth
      PaperProps={{
        style: {
          maxWidth: '1200px',
        },
      }}
    >
      <DialogTitle id="scroll-dialog-title">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2">
            <b>{prefill ? "Duplicate Bundle" : "Create Bundle"}</b>
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={onClose}
            style={{ padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Formik
        // enableReinitialize: the dialog is always mounted by the container, so switching
        // between a plain "Add Bundle" (prefill null) and a "Duplicate" (prefill set) has to
        // re-seed the form. Formik deep-compares initialValues, so the inline object here
        // only resets the form when the prefill actually changes.
        enableReinitialize
        initialValues={{
          name: prefill ? prefill.name : "",
          description: prefill ? prefill.description : "",
          moduleIds: prefill ? prefill.moduleIds : [],
        }}
        validate={validate}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <DialogContent dividers className={classes.scrollContainer}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                variant="outlined"
                value={formik.values.name}
                error={Boolean(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <Box pt={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description"
                  name="description"
                  variant="outlined"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </Box>
              <Box py={2}>
                <Divider />
              </Box>
              <Box pb={1}>
                <Typography variant="body2">
                  <b>Modules</b>
                  {" "}
                  <Typography variant="caption" color="textSecondary" component="span">
                    {formik.values.moduleIds.length}
                    {" selected"}
                  </Typography>
                </Typography>
              </Box>
              {prefill && (
                <Box pb={1}>
                  <Typography variant="caption" color="error">
                    {`Copied from "${prefill.sourceName}". Change the module set before saving — two bundles may not hold the same modules.`}
                  </Typography>
                </Box>
              )}
              {groupByCategory(moduleListing).map(([category, mods]) => (
                <Box key={category} mb={2}>
                  <Typography variant="overline" color="textSecondary">
                    {category}
                    {" "}
                    {mods.filter((m) => formik.values.moduleIds.includes(m.id)).length}
                    {" of "}
                    {mods.length}
                    {" on"}
                  </Typography>
                  <Grid container spacing={1}>
                    {mods.map((m) => {
                      const on = formik.values.moduleIds.includes(m.id);
                      return (
                        <Grid item xs={12} sm={6} key={m.id}>
                          <Paper
                            variant="outlined"
                            className={on ? classes.moduleCardOn : classes.moduleCard}
                            onClick={() => formik.setFieldValue(
                              "moduleIds",
                              on
                                ? formik.values.moduleIds.filter((x) => x !== m.id)
                                : [...formik.values.moduleIds, m.id]
                            )}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body2">
                                  {m.name}
                                  {m.tier === "addon" && <Chip size="small" label="add-on" className={classes.badge} />}
                                  {m.tier === "client" && <Chip size="small" label="client" className={classes.badge} />}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">{m.description}</Typography>
                              </Box>
                              <Switch checked={on} readOnly />
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button
                variant="text"
                type="button"
                disabled={isLoading}
                onClick={onClose}
                style={{ color: "black" }}
              >
                Cancel
              </Button>
              <Button variant="contained" type="submit" disabled={isLoading}>
                Save
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
